"use client";

import { Button } from "@/components/shared/button";
import { LoadingSpinner } from "@/components/shared/loading-spinner";
import { Card, CardContent } from "@/components/ui/card";
import {
  useAuthContext,
  useAuthVerify,
  useAuthVerifyRetry,
  VerifyError,
} from "@/providers/auth-provider";
import { useCallback, useEffect, useRef, useState } from "react";

export const VerifyForm = () => {
  const authContext = useAuthContext();
  const verify = useAuthVerify();
  const { authVerifyRetry } = useAuthVerifyRetry();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [isCodeComplete, setIsCodeComplete] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const isSubmitting = authContext.verify.loading;
  const hasError = authContext.verify.error;
  const maxResends = 3;
  const canResend = resendTimer === 0 && resendCount < maxResends;

  const handleSubmit = useCallback(
    async (codeString: string) => {
      if (codeString.length === 6 && !hasSubmitted) {
        setHasSubmitted(true);
        verify.authVerify({ otpCode: codeString });
      }
    },
    [verify, hasSubmitted],
  );

  // Start resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Reset hasSubmitted when code changes after an error
  useEffect(() => {
    if (hasError !== undefined && hasSubmitted) {
      setHasSubmitted(false);
    }
  }, [hasError, hasSubmitted]);

  // Check if code is complete and auto-submit
  useEffect(() => {
    const fullCode = code.join("");
    const isComplete = fullCode.length === 6;
    setIsCodeComplete(isComplete);

    // Auto-submit when 6 digits are entered, not already submitting, no error, and haven't submitted this code yet
    if (
      isComplete &&
      !isSubmitting &&
      hasError === undefined &&
      !hasSubmitted
    ) {
      handleSubmit(fullCode);
    }
  }, [code, isSubmitting, hasError, hasSubmitted, handleSubmit]);

  // Focus first empty input on error clear
  useEffect(() => {
    if (hasError === undefined) {
      const firstEmptyIndex = code.findIndex((digit) => digit === "");
      if (firstEmptyIndex !== -1) {
        inputRefs.current[firstEmptyIndex]?.focus();
      }
    }
  }, [hasError, code]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    // Reset submission state when user modifies code
    if (hasSubmitted) {
      setHasSubmitted(false);
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      if (!code[index] && index > 0) {
        // If current input is empty, move to previous and clear it
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputRefs.current[index - 1]?.focus();
      } else if (code[index]) {
        // If current input has value, clear it
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");
    const digits = paste.replace(/\D/g, "").slice(0, 6);

    if (digits.length > 0) {
      const newCode = Array(6).fill("");
      for (let i = 0; i < digits.length && i < 6; i++) {
        newCode[i] = digits[i];
      }
      setCode(newCode);

      // Focus the next empty input or the last one
      const nextIndex = Math.min(digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    await authVerifyRetry();
    setResendTimer(30); // 30 second cooldown
    setResendCount((prev) => prev + 1);
    setCode(["", "", "", "", "", ""]);
    inputRefs.current[0]?.focus();
  };

  return (
    <Card className="max-w-md mx-auto">
      <CardContent className="space-y-6">
        {/* OTP Input */}
        <div className="flex gap-2 justify-center">
          {code.map((digit, index) => (
            <input
              key={`otp-input-${
                // biome-ignore lint/suspicious/noArrayIndexKey: OK for fixed length array like OTP inputs
                index
              }`}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={isSubmitting}
              className={`w-10 h-10 md:w-12 md:h-12 text-center text-lg font-medium border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                hasError !== undefined
                  ? "border-red-300 bg-red-50"
                  : digit
                    ? "border-green-300 bg-green-50"
                    : "border-gray-300"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            />
          ))}
        </div>

        {/* Error Message */}
        {hasError !== undefined && (
          <div className="text-center">
            <p className="text-sm text-red-600">
              {hasError === VerifyError.FAILED_ATTEMPT
                ? "Invalid verification code"
                : "Verification failed"}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {authContext.verify.attempts} attempts remaining
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button
            onClick={() => handleSubmit(code.join(""))}
            disabled={isSubmitting || !isCodeComplete}
            className="w-full"
            variant={isCodeComplete ? "active" : "muted"}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Verifying...
              </div>
            ) : isCodeComplete ? (
              "Continue"
            ) : (
              "Enter OTP to continue"
            )}
          </Button>

          {/* Terms */}
          <div className="text-center text-xxs text-[rgba(33,33,33,0.5)]">
            By continuing, you acknowledge At Last's{" "}
            <a
              href="https://www.atlast.co/legal/customer-terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-700"
            >
              Terms of Service
            </a>{" "}
            &{" "}
            <a
              href="https://www.atlast.co/legal/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-gray-700"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>

        {/* Resend Section */}
        <div className="text-center border-t pt-4">
          <p className="text-xs text-black-50">Didn't receive the code?</p>
          <Button
            onClick={handleResend}
            disabled={!canResend || isSubmitting}
            variant="ghost"
            size="sm"
          >
            {resendTimer > 0
              ? `Resend in ${resendTimer}s`
              : resendCount >= maxResends
                ? "Max resends reached"
                : "Resend code"}
          </Button>
          {resendCount > 0 && resendCount < maxResends && (
            <p className="text-xs text-gray-500 mt-1">
              {maxResends - resendCount} resends remaining
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
